import { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export function useKeyboardShortcuts() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // ✅ Guard: Some keyboard events don't have a key property
      if (!event.key) return;

      const target = event.target as HTMLElement | null;
      const isTypingContext =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        Boolean(target?.isContentEditable);

      if (isTypingContext) return;
      
      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const modifierKey = isMac ? event.metaKey : event.ctrlKey;
      const key = event.key.toLowerCase();

      // Cmd/Ctrl + K - Command palette
      if (key === "k" && modifierKey) {
        event.preventDefault();
        // TODO: Implement command palette UI
        return;
      }

      // Cmd/Ctrl + H - Landing (signed out) or first authenticated step
      if (key === "h" && modifierKey) {
        event.preventDefault();
        navigate(isAuthenticated ? "/preferences" : "/");
        return;
      }

      // Cmd/Ctrl + P - Preferences (authenticated only)
      if (key === "p" && modifierKey && isAuthenticated) {
        event.preventDefault();
        navigate("/preferences");
        return;
      }

      // Cmd/Ctrl + W - Weekly Planner (authenticated only)
      if (key === "w" && modifierKey && isAuthenticated) {
        event.preventDefault();
        navigate("/weekly-planner");
        return;
      }

      // Cmd/Ctrl + S - Shopping List (authenticated only)
      if (key === "s" && modifierKey && isAuthenticated) {
        event.preventDefault();
        navigate("/shopping-list");
        return;
      }
    },
    [navigate, isAuthenticated]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return {};
}

/**
 * Hook to get keyboard shortcut label for current platform
 */
export function useKeyboardShortcutLabel(shortcut: { meta?: boolean; ctrl?: boolean; key: string }): string {
  const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;

  const modifierKey = shortcut.meta || shortcut.ctrl ? (isMac ? "⌘" : "Ctrl+") : "";
  const key = shortcut.key.toUpperCase();

  return `${modifierKey}${key}`;
}
